import { ref, onUnmounted, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useRegisterSW } from "virtual:pwa-register/vue";
import { useAwaitAction } from "./use-await-action";
import {
  setIncrementalInterval,
  type IncrementalIntervalCancelFn,
} from "@/utils/set-incremental-interval";

const UPDATE_CHECK_INTERVALS_MS = {
  0: 1_000,
  1: 5_000,
  2: 10_000,
  3: 30_000,
};

const SW_SCRIPT_PATH = "/sw.js";
const MAX_CONSECUTIVE_FAILURES_BEFORE_SILENT_MODE = 3;

let consecutiveUpdateFailures = 0;

export interface ServiceWorkerUpdateState {
  needRefresh: boolean;
  offlineReady: boolean;
  updateServiceWorker: () => Promise<void>;
}

interface ServiceWorkerHealthStatus {
  isHealthy: boolean;
  scriptAccessible: boolean;
  registrationValid: boolean;
  error?: string;
}

async function isServiceWorkerScriptAccessible(): Promise<boolean> {
  try {
    const response = await fetch(SW_SCRIPT_PATH, {
      method: "HEAD",
      cache: "no-store",
    });
    return response.ok;
  } catch (error) {
    console.warn(
      "[Service Worker] Script accessibility check failed:",
      error instanceof Error ? error.message : error,
    );
    return false;
  }
}

function isRegistrationHealthy(
  registration: ServiceWorkerRegistration,
): boolean {
  const { active, waiting, installing } = registration;
  const hasAnyWorker = !!(active || waiting || installing);

  if (!hasAnyWorker) {
    return false;
  }

  if (active) {
    try {
      const scriptURL = active.scriptURL;
      const isScriptURLInvalid =
        !scriptURL || scriptURL === "Unknown" || scriptURL === "";

      if (isScriptURLInvalid) {
        console.warn(
          "[Service Worker] Active worker has invalid scriptURL:",
          scriptURL,
        );
        return false;
      }
    } catch {
      return false;
    }
  }

  return true;
}

function canRegistrationUpdate(
  registration: ServiceWorkerRegistration,
): boolean {
  try {
    if (!registration.scope) {
      return false;
    }

    const activeWorker = registration.active;
    if (activeWorker) {
      const scriptURL = activeWorker.scriptURL;
      if (!scriptURL || scriptURL === "Unknown") {
        return false;
      }
    }

    return true;
  } catch {
    return false;
  }
}

async function checkServiceWorkerHealth(): Promise<ServiceWorkerHealthStatus> {
  if (!("serviceWorker" in navigator)) {
    return {
      isHealthy: false,
      scriptAccessible: false,
      registrationValid: false,
      error: "Service workers not supported",
    };
  }

  const scriptAccessible = await isServiceWorkerScriptAccessible();
  if (!scriptAccessible) {
    return {
      isHealthy: false,
      scriptAccessible: false,
      registrationValid: false,
      error: "Service worker script not accessible",
    };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
      return {
        isHealthy: false,
        scriptAccessible: true,
        registrationValid: false,
        error: "No registration found",
      };
    }

    const registrationValid = isRegistrationHealthy(registration);
    return {
      isHealthy: scriptAccessible && registrationValid,
      scriptAccessible,
      registrationValid,
      error: registrationValid ? undefined : "Registration in invalid state",
    };
  } catch (error) {
    return {
      isHealthy: false,
      scriptAccessible: true,
      registrationValid: false,
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

async function safeUpdateServiceWorker(
  registration: ServiceWorkerRegistration,
): Promise<boolean> {
  if (
    consecutiveUpdateFailures >= MAX_CONSECUTIVE_FAILURES_BEFORE_SILENT_MODE
  ) {
    if (
      consecutiveUpdateFailures === MAX_CONSECUTIVE_FAILURES_BEFORE_SILENT_MODE
    ) {
      console.warn(
        "[Service Worker] Too many consecutive update failures. Updates will be silently skipped until page reload.",
      );
      consecutiveUpdateFailures++;
    }
    return false;
  }

  if (!canRegistrationUpdate(registration)) {
    consecutiveUpdateFailures++;
    console.warn(
      "[Service Worker] Skipping update - registration in invalid state. The service worker may need recovery.",
    );
    return false;
  }

  const scriptAccessible = await isServiceWorkerScriptAccessible();
  if (!scriptAccessible) {
    consecutiveUpdateFailures++;
    console.warn(
      "[Service Worker] Skipping update - script not accessible. This may be due to a deployment in progress or network issues.",
    );
    return false;
  }

  try {
    await registration.update();
    consecutiveUpdateFailures = 0;
    return true;
  } catch (error) {
    consecutiveUpdateFailures++;

    const errorMessage = error instanceof Error ? error.message : String(error);
    const isKnownRecoverableError =
      errorMessage.includes("Failed to update") ||
      errorMessage.includes("Not found") ||
      errorMessage.includes("invalid state") ||
      errorMessage.includes("Unknown");

    if (isKnownRecoverableError) {
      console.warn(
        "[Service Worker] Update failed (recoverable):",
        errorMessage,
      );
    } else {
      console.error("[Service Worker] Unexpected update error:", error);
    }

    return false;
  }
}

function resetUpdateFailureCounter(): void {
  consecutiveUpdateFailures = 0;
}

async function attemptServiceWorkerRecovery(): Promise<boolean> {
  console.log("[Service Worker] Attempting recovery...");

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();

    for (const registration of registrations) {
      console.log(
        `[Service Worker] Unregistering scope: ${registration.scope}`,
      );
      await registration.unregister();
    }

    const cacheKeys = await caches.keys();
    const serviceWorkerCacheKeys = cacheKeys.filter(
      (key) =>
        key.includes("workbox") ||
        key.includes("sw-") ||
        key.includes("fanfest"),
    );

    for (const key of serviceWorkerCacheKeys) {
      console.log(`[Service Worker] Clearing cache: ${key}`);
      await caches.delete(key);
    }

    resetUpdateFailureCounter();
    console.log("[Service Worker] Recovery complete - page reload recommended");
    return true;
  } catch (error) {
    console.error("[Service Worker] Recovery failed:", error);
    return false;
  }
}

// Time in ms to wait before considering the page "settled"
// Updates detected before this will auto-reload without prompting
const INITIAL_LOAD_GRACE_PERIOD_MS = 1_000;
const SW_AUTO_RELOAD_KEY = "sw-auto-reload-attempted";
const SW_MANUAL_REFRESH_TIMESTAMP_KEY = "sw-manual-refresh-timestamp";
const MANUAL_REFRESH_COOLDOWN_MS = 30_000;
const UPDATE_BUTTON_DELAY_MS = 1_000;
const SW_ASSETS_OUTDATED_KEY = "sw-assets-outdated-reload";

export function useServiceWorker() {
  const needRefresh = ref(false);
  const offlineReady = ref(false);
  const updateAvailable = ref(false);
  const healthStatus = ref<ServiceWorkerHealthStatus | null>(null);
  const isInitialLoad = ref(true);

  const router = useRouter();
  let updateCheckIntervalCancelFn: IncrementalIntervalCancelFn | null = null;

  const {
    offlineReady: swOfflineReady,
    needRefresh: swNeedRefresh,
    updateServiceWorker,
  } = useRegisterSW({
    immediate: true,
    onRegisteredSW(swUrl, registration) {
      console.log(`[Service Worker] Registered at ${swUrl}`);

      if (registration && import.meta.env.PROD) {
        updateCheckIntervalCancelFn = setIncrementalInterval(async () => {
          console.log("[Service Worker] Checking for updates...");

          const updated = await safeUpdateServiceWorker(registration);
          if (!updated) {
            console.log(
              "[Service Worker] Update skipped due to health check failure",
            );
          }
        }, UPDATE_CHECK_INTERVALS_MS);
      }
    },
    onRegisterError(error) {
      console.error("[Service Worker] Registration error:", error);

      const errorMessage =
        error instanceof Error ? error.message : String(error);
      const isMissingScriptError =
        errorMessage.includes("Failed to update") ||
        errorMessage.includes("Not found") ||
        errorMessage.includes("400");

      if (isMissingScriptError) {
        console.log(
          "[Service Worker] Registration failed due to missing script - this may resolve after deployment completes",
        );
      }
    },
  });

  onMounted(async () => {
    // Listen for deployment mismatch signals from the service worker.
    // When a JS/CSS request gets an HTML response (SPA fallback for removed chunks),
    // the SW sends ASSETS_OUTDATED so we can reload with fresh HTML.
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.addEventListener("message", (event) => {
        if (event.data?.type === "ASSETS_OUTDATED") {
          if (sessionStorage.getItem(SW_ASSETS_OUTDATED_KEY)) return;
          sessionStorage.setItem(SW_ASSETS_OUTDATED_KEY, "true");
          console.log(
            "[Service Worker] Assets outdated — reloading for fresh content",
          );
          window.location.reload();
        }
      });
    }

    healthStatus.value = await checkServiceWorkerHealth();

    if (!healthStatus.value.isHealthy) {
      console.warn(
        "[Service Worker] Initial health check failed:",
        healthStatus.value.error,
      );
    }

    const canWarmUpAssets =
      healthStatus.value.isHealthy &&
      "serviceWorker" in navigator &&
      navigator.serviceWorker.controller;

    if (canWarmUpAssets) {
      navigator.serviceWorker.controller!.postMessage({
        type: "WARM_UP_ALL_ASSETS",
      });
    }

    // After grace period, mark the page as settled
    // Updates detected after this will prompt the user instead of auto-reloading
    setTimeout(() => {
      isInitialLoad.value = false;
      // Clear the auto-reload flag so future navigations can auto-reload if needed
      sessionStorage.removeItem(SW_AUTO_RELOAD_KEY);
      console.log(
        "[Service Worker] Initial load grace period ended, will prompt for future updates",
      );
    }, INITIAL_LOAD_GRACE_PERIOD_MS);
  });

  const stopWatchNeedRefresh = watch(
    swNeedRefresh,
    (value) => {
      if (value) {
        updateAvailable.value = true;

        // Check if we're in the cooldown period after a manual refresh
        const lastManualRefresh = sessionStorage.getItem(
          SW_MANUAL_REFRESH_TIMESTAMP_KEY,
        );
        if (lastManualRefresh) {
          const elapsed = Date.now() - parseInt(lastManualRefresh, 10);
          if (elapsed < MANUAL_REFRESH_COOLDOWN_MS) {
            console.log(
              `[Service Worker] Update detected but suppressing banner (${Math.round((MANUAL_REFRESH_COOLDOWN_MS - elapsed) / 1000)}s remaining in cooldown)`,
            );
            return;
          }
          // Cooldown expired, clear the timestamp
          sessionStorage.removeItem(SW_MANUAL_REFRESH_TIMESTAMP_KEY);
        }

        // If we're still in the initial load phase, auto-reload without prompting
        // But only if we haven't already attempted an auto-reload this session
        const alreadyAttemptedReload =
          sessionStorage.getItem(SW_AUTO_RELOAD_KEY) === "true";

        if (isInitialLoad.value && !alreadyAttemptedReload) {
          console.log(
            "[Service Worker] Update available during initial load, auto-reloading...",
          );
          sessionStorage.setItem(SW_AUTO_RELOAD_KEY, "true");

          // Send SKIP_WAITING but don't let the plugin reload — we wait for
          // controllerchange so the new SW is active before reloading.
          // This prevents stale HTML from referencing non-existent JS chunks.
          updateServiceWorker(false).catch((error) => {
            console.warn("[Service Worker] Update request failed:", error);
          });

          const reloadTimeout = setTimeout(
            () => window.location.reload(),
            3000,
          );
          navigator.serviceWorker.addEventListener(
            "controllerchange",
            () => {
              clearTimeout(reloadTimeout);
              window.location.reload();
            },
            { once: true },
          );
          return;
        }

        // Page is settled, show the prompt to the user
        needRefresh.value = value;
        console.log(
          "[Service Worker] New content available, prompting user to refresh",
        );
      } else {
        needRefresh.value = value;
      }
    },
    { immediate: true },
  );

  const stopWatchOfflineReady = watch(
    swOfflineReady,
    (value) => {
      offlineReady.value = value;
      if (value) {
        console.log("[Service Worker] App ready to work offline");
      }
    },
    { immediate: true },
  );

  const unregisterRouterHook = router.afterEach(async () => {
    const hasActiveServiceWorker =
      "serviceWorker" in navigator && navigator.serviceWorker.controller;

    if (!hasActiveServiceWorker) {
      return;
    }

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        return;
      }

      console.log("[Service Worker] Checking for updates on route navigation");
      await safeUpdateServiceWorker(registration);
    } catch (error) {
      console.warn(
        "[Service Worker] Navigation update check error:",
        error instanceof Error ? error.message : error,
      );
    }
  });

  onUnmounted(() => {
    stopWatchNeedRefresh();
    stopWatchOfflineReady();
    unregisterRouterHook();
    updateCheckIntervalCancelFn?.();
  });

  const close = async () => {
    updateAvailable.value = false;
    needRefresh.value = false;
    offlineReady.value = false;
  };

  const { handler: updateAndReload, loading: isUpdating } = useAwaitAction(
    async () => {
      console.log("[Service Worker] Updating service worker and reloading...");

      sessionStorage.setItem(
        SW_MANUAL_REFRESH_TIMESTAMP_KEY,
        Date.now().toString(),
      );

      // Wait for the new SW to take control before reloading,
      // with a timeout fallback for Safari and other browsers
      // that may not fire controllerchange reliably.
      const swReady = new Promise<void>((resolve) => {
        const timeout = setTimeout(resolve, UPDATE_BUTTON_DELAY_MS);
        if ("serviceWorker" in navigator) {
          navigator.serviceWorker.addEventListener(
            "controllerchange",
            () => {
              clearTimeout(timeout);
              resolve();
            },
            { once: true },
          );
        }
      });

      // Send SKIP_WAITING to the waiting SW
      updateServiceWorker(false).catch((error) => {
        console.warn("[Service Worker] Update request failed:", error);
      });

      await swReady;
      window.location.reload();
    },
  );

  const checkHealthWithOptionalRecovery = async (attemptRecovery = false) => {
    healthStatus.value = await checkServiceWorkerHealth();

    if (!healthStatus.value.isHealthy && attemptRecovery) {
      const recovered = await attemptServiceWorkerRecovery();
      if (recovered) {
        healthStatus.value = await checkServiceWorkerHealth();
      }
    }

    return healthStatus.value;
  };

  return {
    needRefresh,
    offlineReady,
    updateAvailable,
    isUpdating,
    healthStatus,
    updateServiceWorker: updateAndReload,
    checkHealth: checkHealthWithOptionalRecovery,
    attemptRecovery: attemptServiceWorkerRecovery,
    resetFailureCounter: resetUpdateFailureCounter,
    close,
  };
}
